import { useState } from 'react'
import { X, Download, Smartphone, Wifi, WifiOff } from 'lucide-react'
import { usePWA } from '@/hooks/usePWA'
import { EnhancedButton } from '@/components/ui/enhanced-button'
import { InteractiveCard } from '@/components/ui/interactive-card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

export const PWAInstallBanner = () => {
  const [isHidden, setIsHidden] = useState(() => {
    // Reset banner visibility on each page load for debugging
    localStorage.removeItem('pwa-banner-hidden')
    return false
  })
  const { 
    isInstallable, 
    isInstalled, 
    isOnline, 
    notificationPermission,
    installApp, 
    requestNotificationPermission 
  } = usePWA()

  // Debug logging
  console.log('PWA Install Banner Debug:', { isHidden, isInstalled, isInstallable })

  if (isHidden || isInstalled || !isInstallable) return null

  const handleInstall = async () => {
    const success = await installApp()
    if (success) {
      setIsHidden(true)
    }
  }

  const handleEnableNotifications = async () => {
    await requestNotificationPermission()
  }

  return (
    <InteractiveCard 
      className={cn(
        "fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-sm md:max-w-md",
        "bg-gradient-to-r from-primary to-primary-glow text-primary-foreground",
        "shadow-xl border-0 animate-fade-in",
        "mb-safe-bottom" // Safe area for mobile devices
      )}
      hover={false}
      glow={true}
    >
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            <h3 className="font-semibold text-sm">Fair-Stays App installieren</h3>
          </div>
          <EnhancedButton
            variant="ghost"
            size="icon-sm"
            onClick={() => setIsHidden(true)}
            className="text-primary-foreground hover:bg-white/20"
          >
            <X className="h-4 w-4" />
          </EnhancedButton>
        </div>

        <div className="space-y-3">
          <p className="text-sm text-primary-foreground/90">
            Installieren Sie Fair-Stays für ein App-ähnliches Erlebnis mit Offline-Zugriff und Benachrichtigungen.
          </p>

          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary" className="text-xs bg-white/20 text-primary-foreground">
              <Wifi className="h-3 w-3 mr-1" />
              Offline verfügbar
            </Badge>
            <Badge variant="secondary" className="text-xs bg-white/20 text-primary-foreground">
              📱 App-Erlebnis
            </Badge>
            <Badge variant="secondary" className="text-xs bg-white/20 text-primary-foreground">
              🔔 Push-Benachrichtigungen
            </Badge>
          </div>

          <div className="flex gap-2">
            <EnhancedButton
              onClick={handleInstall}
              variant="secondary"
              size="sm"
              className="flex-1 bg-white text-primary hover:bg-white/90"
              ripple={true}
              bounce={true}
            >
              <Download className="h-4 w-4 mr-2" />
              Installieren
            </EnhancedButton>

            {notificationPermission !== 'granted' && (
              <EnhancedButton
                onClick={handleEnableNotifications}
                variant="ghost"
                size="sm"
                className="text-primary-foreground hover:bg-white/20"
              >
                🔔 Erlauben
              </EnhancedButton>
            )}
          </div>
        </div>
      </div>
    </InteractiveCard>
  )
}

export const PWAStatus = () => {
  const { isOnline, isInstalled } = usePWA()

  return (
    <div className="flex items-center gap-2">
      <Badge 
        variant={isOnline ? "default" : "destructive"} 
        className="text-xs"
      >
        {isOnline ? (
          <>
            <Wifi className="h-3 w-3 mr-1" />
            Online
          </>
        ) : (
          <>
            <WifiOff className="h-3 w-3 mr-1" />
            Offline
          </>
        )}
      </Badge>

      {isInstalled && (
        <Badge variant="secondary" className="text-xs">
          <Smartphone className="h-3 w-3 mr-1" />
          App-Modus
        </Badge>
      )}
    </div>
  )
}